import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, TextField, Button, Stack, Typography, Grid, MenuItem,
  Switch, FormControlLabel, CircularProgress, Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import { getCouponDetail, createCoupon, updateCoupon } from '../../api/endpoints/coupons';

const initialForm = {
  code: '',
  description: '',
  discountType: 'percentage',
  discountValue: '',
  minOrderAmount: 0,
  maxDiscount: 0,
  maxUses: 0,
  maxUsesPerUser: 0,
  validFrom: '',
  validUntil: '',
  isActive: true,
  appliesTo: 'all',
  specificUsers: '',
};

const CouponForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit) {
      (async () => {
        try {
          const res = await getCouponDetail(id);
          const c = res.data.data;
          setForm({
            code: c.code || '',
            description: c.description || '',
            discountType: c.discountType || 'percentage',
            discountValue: c.discountValue || '',
            minOrderAmount: c.minOrderAmount || 0,
            maxDiscount: c.maxDiscount || 0,
            maxUses: c.maxUses || 0,
            maxUsesPerUser: c.maxUsesPerUser || 0,
            validFrom: c.validFrom ? c.validFrom.slice(0, 16) : '',
            validUntil: c.validUntil ? c.validUntil.slice(0, 16) : '',
            isActive: c.isActive ?? true,
            appliesTo: c.appliesTo || 'all',
            specificUsers: c.specificUsers?.join(', ') || '',
          });
        } catch (err) {
          toast.error('Failed to load coupon');
          navigate('/shop/coupons');
        } finally {
          setFetchLoading(false);
        }
      })();
    }
  }, [id, isEdit, navigate]);

  const handleChange = (field) => (e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.code.trim()) errs.code = 'Code is required';
    if (!form.discountValue || Number(form.discountValue) < 1) errs.discountValue = 'Valid discount value is required';
    if (!form.validFrom) errs.validFrom = 'Valid from date is required';
    if (!form.validUntil) errs.validUntil = 'Valid until date is required';
    if (form.validFrom && form.validUntil && new Date(form.validFrom) >= new Date(form.validUntil)) {
      errs.validUntil = 'End date must be after start date';
    }
    if (form.discountType === 'percentage' && Number(form.discountValue) > 100) {
      errs.discountValue = 'Percentage cannot exceed 100';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        discountValue: Number(form.discountValue),
        minOrderAmount: Number(form.minOrderAmount),
        maxDiscount: Number(form.maxDiscount),
        maxUses: Number(form.maxUses),
        maxUsesPerUser: Number(form.maxUsesPerUser),
        validFrom: new Date(form.validFrom).toISOString(),
        validUntil: new Date(form.validUntil).toISOString(),
        specificUsers: form.appliesTo === 'specific_users'
          ? form.specificUsers.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      };
      if (isEdit) {
        await updateCoupon(id, payload);
        toast.success('Coupon updated');
      } else {
        await createCoupon(payload);
        toast.success('Coupon created');
      }
      navigate('/shop/coupons');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save coupon');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={isEdit ? 'Edit Coupon' : 'Create Coupon'}
        subtitle={isEdit ? `Editing coupon ${form.code}` : 'Create a new discount coupon'}
      />
      <Paper sx={{ p: 3, maxWidth: 700 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Coupon Code" value={form.code} onChange={handleChange('code')}
                error={!!errors.code} helperText={errors.code} required disabled={isEdit} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Description" value={form.description} onChange={handleChange('description')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth select label="Discount Type" value={form.discountType} onChange={handleChange('discountType')}>
                <MenuItem value="percentage">Percentage (%)</MenuItem>
                <MenuItem value="flat">Flat (₹)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Discount Value" type="number" value={form.discountValue}
                onChange={handleChange('discountValue')} error={!!errors.discountValue} helperText={errors.discountValue} required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Max Discount (₹)" type="number" value={form.maxDiscount}
                onChange={handleChange('maxDiscount')} helperText="0 = unlimited (for %)" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Min Order Amount (₹)" type="number" value={form.minOrderAmount}
                onChange={handleChange('minOrderAmount')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Max Total Uses" type="number" value={form.maxUses}
                onChange={handleChange('maxUses')} helperText="0 = unlimited" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Max Uses Per User" type="number" value={form.maxUsesPerUser}
                onChange={handleChange('maxUsesPerUser')} helperText="0 = unlimited" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="Applies To" value={form.appliesTo} onChange={handleChange('appliesTo')}>
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="specific_users">Specific Users</MenuItem>
              </TextField>
            </Grid>
            {form.appliesTo === 'specific_users' && (
              <Grid item xs={12}>
                <TextField fullWidth label="User IDs (comma-separated)" value={form.specificUsers}
                  onChange={handleChange('specificUsers')} helperText="Enter user ObjectIds separated by commas" />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Valid From" type="datetime-local" value={form.validFrom}
                onChange={handleChange('validFrom')} InputLabelProps={{ shrink: true }}
                error={!!errors.validFrom} helperText={errors.validFrom} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Valid Until" type="datetime-local" value={form.validUntil}
                onChange={handleChange('validUntil')} InputLabelProps={{ shrink: true }}
                error={!!errors.validUntil} helperText={errors.validUntil} required />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm(p => ({ ...p, isActive: e.target.checked }))} />} label="Active" />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              {isEdit ? 'Update Coupon' : 'Create Coupon'}
            </Button>
            <Button variant="outlined" onClick={() => navigate('/shop/coupons')}>Cancel</Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default CouponForm;
